<template>
    <div>
        <div v-if="!isActive" class="h-3"></div>

        <delivery-schedule-address
            v-if="showDropdown"
            :show-dropdown="showDropdown"
            :address="address"
            address-class="px-4"/>
        <div v-else class="h-1 lg:h-2"></div>

        <div class="container mx-auto text-center max-w-xs pt-4">
            <div class="deliveryAddressCard__container">
                <div
                    v-if="!showEditForm && !showPauseForm"
                    class="accordion__sm--no-scroll accordion__sm accordion__sm--active">

                    <!-- Pause -->
                    <pause-button
                        v-if="isDEU"
                        :subscription="subscription"
                        @paused="togglePauseForm"/>

                    <!-- Quantity -->
                    <address-manage-quantity
                        v-if="isActive"
                        :subscription="subscription"
                        :label="$t('MANAGE_DELIVERY_BOXES_PER')"/>

                    <delivery-price headingKey="MANAGE_DELIVERY_COST_EACH" v-if="subscriptionsPrice" :price="subscriptionsPrice"/>

                    <!-- Change Plan -->
                    <div class="deliveryAddressCard__action edit-option" @click="openModal()">
                        <p v-if="!loading && offeredPlan" class="no-margin" v-text="$t('MANAGE_DELIVERY_PLAN', { planName: offeredPlan.name })"></p>
                        <i v-if="loading" class="loader__loading-icon fa-spin"/>
                        <i class="fas fa-exchange-alt"></i>
                    </div>

                    <modal v-if="modalOpen" :allowCancel="false" custom-class="max-w-sm" @closed="closeModal()">
                        <div class="shift-delivery-modal" >
                            <div class="shift-delivery-content">
                                <p v-html="$t('SHIFT_DELIVERY_PLAN_SUCCESS', { plan: offeredPlan.name })"></p>
                                <p><strong>{{formatSelectedNextDeliveryDate}}</strong></p>
                                <button
                                    type="button"
                                    class="btn btn--primary btn--half"
                                    @click="updatePlan()"
                                    v-text="$t('SHIFT_DELIVERY_SAVE')">
                                </button>
                            </div>
                        </div>
                    </modal>

                    <!-- Edit address -->
                    <div v-if="isActive" class="deliveryAddressCard__action edit-option" @click="toggleEditForm">
                        <p class="no-margin" v-text="$t('MANAGE_DELIVERY_EDIT')"></p>
                        <img class="edit-option__icon" src="@/assets/images/decoration/arrow-right-black.svg">
                    </div>

                    <!-- Delivery instructions for US -->
                    <div v-if="isUSA" class="edit-instructions">
                        <p class="no-margin" v-html="$t('MANAGE_DELIVERY_INSTRUCTIONS_UPS')"></p>
                    </div>

                    <!-- Cancel -->
                    <div class="deliveryAddressCard__action border-t" v-if="!getPauseIsCancel && allowCancel" @click="toggleCancelModal">
                        <p class="no-margin" v-text="$t('MANAGE_DELIVERY_CANCEL')"></p>
                        <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/nav-close.svg">
                    </div>

                    <!-- Legal Cancellation for US-->
                    <div v-if="getPauseIsCancel && !isPaused" class="deliveryAddressCard__action border-t" @click="pauseSubscription">
                        <p class="no-margin" v-text="$t('MANAGE_DELIVERY_CANCEL')"></p>
                        <img class="cancel-option__icon" src="@/assets/images/decoration/nav-close.svg">
                    </div>

                    <resume-deliveries
                        v-if="getPauseIsCancel && isPaused"
                        :subscription="subscription"
                        @deliveries-resumed="handleDeliveriesResumed"/>
                </div>

                <edit-delivery-address-form
                    v-if="showEditForm"
                    :subscription-id="subscription.id"
                    :address="address"
                    :delivery-comment="subscription.delivery_comment"
                    :property-type="address.property_type"
                    @subscription-address-updated="addressUpdated"
                    @closed="toggleEditForm"/>

                <pause-deliveries
                    v-if="showPauseForm"
                    :subscriptionId="subscription.id"
                    :isSubscriptionPaused="false"
                    :addressId="subscription.delivery_address_id"
                    @subscription-updated="handleChanged"
                    @deliveries-paused="handleDeliveriesPaused"
                    @closed="togglePauseForm"/>
            </div>
        </div>

        <pause-deliveries-confirmation-modal @pause-deliveries-modal-closed="handleConfirmationModalClosed" :subscription-id="subscription.id" v-if="showPauseConfirmationModal" :fromStep="fromStep" />

        <cancel-subscription-modal
            v-if="showCancelModal && !getPauseIsCancel"
            :subscription-id="subscription.id"
            @cancelled="handleSubscriptionCancelled"
            @closed="toggleCancelModal"/>

        <cancel-subscription-US
            v-if="showCancelModal && getPauseIsCancel"
            :subscription-id="subscription.id"
            @closed="toggleCancelModal"
            @paused="togglePauseForm"
            :nextDeliveryDate="displayDate"
            :isCutOffTimePassed="isCutOffTimePassed"
            :error="error"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDisplayDate } from '@/helpers/DateHelpers';
import { SUBSCRIPTION_STATUS_ACTIVE, SUBSCRIPTION_STATUS_PAUSED } from '@/constants/Subscription';
import CancelSubscriptionModal from '@/components/modals/CancelSubscriptionModal';
import CancelSubscriptionUS from '@/components/modals/CancelSubscriptionUS';
import PauseDeliveriesConfirmationModal from '@/components/modals/PauseDeliveriesConfirmationModal';
import EditDeliveryAddressForm from '@/components/forms/addresses/EditDeliveryAddressForm';
import DeliveryPrice from '@/components/partials/account/my-delivery-addresses/DeliveryPrice';
import AddressManageQuantity from '@/components/partials/account/my-delivery-addresses/AddressManageQuantity';
import DeliveryScheduleAddress from '@/components/partials/account/my-delivery-schedule/DeliveryScheduleAddress';
import PauseDeliveries from '@/components/partials/account/my-delivery-schedule/manage-delivery/PauseDeliveries';
import ResumeDeliveries from '@/components/partials/account/my-delivery-schedule/manage-delivery/ResumeDeliveries';
import Modal from '@/components/structure/Modal';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import PauseButton from '../my-delivery-addresses/PauseButton';

let subscriptionService = new SubscriptionService();

export default {
    name: 'ManageDelivery',
    components: {
        DeliveryPrice,
        DeliveryScheduleAddress,
        PauseDeliveriesConfirmationModal,
        AddressManageQuantity,
        PauseDeliveries,
        ResumeDeliveries,
        CancelSubscriptionModal,
        CancelSubscriptionUS,
        EditDeliveryAddressForm,
        PauseButton,
        Modal,
    },
    props: {
        showDropdown: {
            required: true,
            type: Boolean,
        },
    },
    data() {
        return {
            showEditForm: false,
            showPauseForm: false,
            showCancelModal: false,
            showPauseConfirmationModal: false,
            loading: false,
            modalOpen: false,
            selectedNextDelivery: null,
            error: null,
            fromStep: '',
        };
    },
    mounted() {
        this.selectedNextDelivery = this.deliveries.find((item) => item.datetime === this.subscription.next_delivery_date);
    },
    computed: {
        ...mapGetters({
            activeSubscriptions: 'subscription/getActiveSubscriptions',
            subscription: 'subscription/getActiveSubscription',
            allowCancel: 'subscription/allowCancel',
            deliveries: 'scheduledOrder/getDeliveries',
            plans: 'cms/getPlans',
            getPauseIsCancel: 'subscription/getPauseIsCancel',
            isUSA: 'localisation/isUSA',
            isDEU: 'localisation/isDEU',
        }),

        offeredPlan() {
            const weeklyPlan = this.plans.find((item) => item.slug === 'weekly');
            const fortnightlyPlan = this.plans.find((item) => item.slug === 'fortnightly');
            return this.subscription && this.subscription.plan_slug === 'weekly' ? fortnightlyPlan : weeklyPlan;
        },

        activeSubscriptions() {
            return this.$store.getters['subscription/getActiveSubscriptions'];
        },

        address() {
            return this.subscription && this.$store.getters['address/getAddressById']
                ? this.$store.getters['address/getAddressById'](this.subscription.delivery_address_id)
                : {};
        },

        allowedDeliveries() {
            return this.deliveries.filter((item) => !item.cut_off_expired && !item.scheduled_order_id)
                .map((item) => {
                    item.human = formatDisplayDate(new Date(item.datetime));
                    return item;
                });
        },

        getPrice() {
            const boxPrice = Number(process.env.VUE_APP_BOX_PRICE);
            const shippingPrice = Number(process.env.VUE_APP_DELIVERY_PRICE || 0);
            return boxPrice + shippingPrice;
        },

        subscriptionsPrice() {
            if (this.subscription && this.subscription.shop_total) {
                const subscriptionTotal = this.subscription.price - this.subscription.shop_total;
                return subscriptionTotal;
            }
            return this.subscription.price || 0;
        },

        isActive() {
            return this.subscription.status === SUBSCRIPTION_STATUS_ACTIVE;
        },
        formatSelectedNextDeliveryDate() {
            return formatDisplayDate(new Date(this.allowedDeliveries[0].datetime));
        },
        isPaused() {
            return this.subscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },

        isCutOffTimePassed() {
            const upcomingDeliveryDate = this.deliveries.filter(item => item.datetime === this.nextDeliveryDate);
            return (upcomingDeliveryDate.length !== 0) && upcomingDeliveryDate[0]?.cut_off_expired;
        },

        nextDeliveryDate() {
            return this.subscription?.next_delivery_date;
        },
        displayDate() {
            return this.nextDeliveryDate && formatDisplayDate(this.nextDeliveryDate);
        },
    },
    methods: {
        openModal() {
            this.modalOpen = true;
        },

        closeModal() {
            this.modalOpen = false;
        },

        updatePlan() {
            this.closeModal();

            if (!this.loading) {
                this.loading = true;

                subscriptionService.updateSubscriptionPlan(this.subscription.id, this.offeredPlan.slug, this.allowedDeliveries[0].datetime).then(() => {
                    this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                    this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
                    this.loading = false;
                }).catch(error => console.log(error.response.data));
            }
        },

        handleChanged() {
            this.$emit('changed');
        },

        handleDeliveriesPaused(whyLeave) {
            this.fromStep = whyLeave;
            this.$emit('paused');
            this.showPauseConfirmationModal = true;
        },

        handleDeliveriesResumed() {
            this.$emit('resumed');
            this.showPauseConfirmationModal = false;
        },

        handleConfirmationModalClosed() {
            this.showPauseConfirmationModal = false;
        },

        handleSubscriptionCancelled(subscriptionId) {
            // we're setting the new active subscription here, because we want to reload scheduled orders
            // for the new active subscription before the request in Vuex (subscription module) will be finished
            let subscription = this.activeSubscriptions.filter(subscription => subscription.id !== subscriptionId)[0];
            this.$store.commit('subscription/setActiveSubscriptionId', subscription.id);

            this.$emit('changed');
        },

        toggleCancelModal() {
            this.showCancelModal = !this.showCancelModal;
        },

        toggleEditForm() {
            this.showPauseForm = false;
            this.showEditForm = !this.showEditForm;
        },

        togglePauseForm() {
            this.showEditForm = false;
            this.showPauseForm = !this.showPauseForm;
        },

        addressUpdated() {
            this.$emit('changed');
            this.$store.dispatch('scheduledOrder/fetchAllScheduledOrders', true);
        },

        pauseSubscription() {
            this.showPauseForm = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.cancel-option {
    padding-right: 21px;
}

.cancel-option__icon {
    max-width: 20px;
    max-height: 20px;
}

.edit-option {
    padding-right: 24px;
    border-top: 1px solid black;
}

.edit-instructions {
    padding: 15px 24px;
    border-top: 1px solid black;
    p{
        font-size: 18px;
    }
}

.edit-option__icon {
    max-width: 25px;
    max-height: 25px;
}

.loader__loading-icon {
    width: 30px;
    height: 30px;
}

.shift-delivery-modal {
    text-align: center;

    button {
        margin: 12px 8px 0px 8px;
    }
}
</style>
